/* API responsável por fornecer o contexto de autenticação para a aplicação. Este contexto permite que os componentes filhos acessem informações de autenticação, como o estado do usuário e funções relacionadas à autenticação, sem a necessidade de passar props manualmente através da árvore de componentes.

*/

/*
Importa três ferramentas do React:
createContext — cria o "canal" de comunicação global
useContext — permite que componentes leiam o canal
useState — gerencia estados reativos
*/
import { createContext, useContext, useState } from "react";

/* Cria o contexto — O contexto fica vazio, não transmite nada. Os componentes podem sintonizar nele, mas só receberão dados quando o Provider for configurado.
*/
const AuthContext = createContext();

// Verifica se o JWT já expirou antes de manter a sessão ativa no frontend.
// Isso evita que a interface continue considerando o usuário autenticado após o token ficar inválido.
const isTokenExpired = (tokenValue) => {
    if (!tokenValue) return true;

    try {
        const parts = tokenValue.split('.');
        if (parts.length < 2) return true;

        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

        if (!payload.exp) return false;

        return Date.now() >= payload.exp * 1000;
    } catch {
        return true;
    }
};



// Recupera o token e os dados do usuário do localStorage, mas limpa a sessão
// automaticamente caso o token esteja ausente ou vencido.
const getStoredAuth = () => {
    const storedToken = localStorage.getItem('viver_token');
    const storedDados = localStorage.getItem('viver_dados');

    if (!storedToken || isTokenExpired(storedToken)) {
        localStorage.removeItem('viver_token');
        localStorage.removeItem('viver_dados');
        return { token: null, dadosUsuario: null };
    }

    return {
        token: storedToken,
        dadosUsuario: storedDados ? JSON.parse(storedDados) : null
    };
};

/* Cria e exporta o Provider — é o componente que transmite os dados pelo context( canal). O children representa todos os componentes filhos que ele envolve, ou seja, toda a aplicação.
*/
export const AuthProvider = ({ children }) => {
    // Ao inicializar, já usa o estado salvo no storage, mas respeitando a expiração do token.
    const { token: storedToken, dadosUsuario: storedDados } = getStoredAuth();

    const [token, setToken] = useState(storedToken);
    const [dadosUsuario, setDadosUsuario] = useState(storedDados);

    //Ao fazer login, o token é armazenado no localStorage e no estado do contexto.
    const login = ({ novoToken, dadosUsuario }) => {
        // ✅ Salva no localStorage para persistir
        localStorage.setItem('viver_token', novoToken);
        localStorage.setItem('viver_dados', JSON.stringify(dadosUsuario));
        // ✅ Atualiza o estado para reatividade
        setToken(novoToken);
        if (dadosUsuario) {
            setDadosUsuario(dadosUsuario);
        }
    };

    //Ao fazer logout, o token é removido do localStorage e o estado do contexto é atualizado para null.
    const logout = () => {
        // ✅ Remove do localStorage
        localStorage.removeItem('viver_token');
        localStorage.removeItem('viver_dados');
        // ✅ Limpa o estado
        setToken(null);
        setDadosUsuario(null);
    };


    /* 
    Renderiza o Provider com o value — é aqui que você define o que será transmitido pelo canal. Qualquer componente filho pode receber token, dadosUsuario, login e logout.
    */

    return (
        //
        <AuthContext.Provider value={{ token, dadosUsuario,  login, logout }}>
            {children}  
        </AuthContext.Provider>
    );
};


/* Cria e exporta um hook personalizado — é um atalho elegante para não precisar escrever useContext(AuthContext) em cada componente. 
 Hook personalizado para acessar o contexto de autenticação em qualquer componente funcional.
 */
export const useAuth = () => useContext(AuthContext);